import { Outlet, useNavigate } from 'react-router-dom-v5-compat';
import {
  ReactElement,
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { OpLayout } from 'new-components/OpLayout/OpLayout';
import { OpContent } from 'new-components/OpContent/OpContent';
import { OpAppHeader } from 'new-components/OpAppHeader/OpAppHeader';
import type { OrgSwitcherProps } from 'new-components/OrgSwitcher/types';
import { OpAppNav } from 'new-components/OpAppNav/OpAppNav';
import { ROUTE_ORG_ID_TEMPLATE } from 'new-components/OpAppScaffold/constants';
import { getOpParams } from 'utils/getOpParams';
import { OpRawMenuItemType } from 'config/createRawMenuItems';
import { OpAppSuspenseLoader } from 'new-components/OpAppScaffold/components/OpAppSuspenseLoader';
import { OpAppBanners } from 'new-components/OpAppBanners/OpAppBanners';

import './OpAppLayout.scss';

interface OpAppLayoutProps {
  homePath: string;
  items: OpRawMenuItemType[];
  title: string;
  orgSwitcherProps?: OrgSwitcherProps;
  banners?: ReactNode;
  footer?: ReactElement;
}

/**
 * Must match the tablet-portrait breakpoint used to reveal the nav via CSS
 * in OpAppNav.scss/OpAppHeader.scss ($tablet-portrait-width).
 */
const TABLET_NAV_MEDIA_QUERY = '(min-width: 768px)';

export const OpAppLayout = ({
  homePath,
  items,
  title,
  orgSwitcherProps,
  banners,
  footer,
}: OpAppLayoutProps) => {
  const { orgId } = getOpParams();
  const navigate = useNavigate();
  const [isShowingNavOnMobile, setIsShowingNavOnMobile] = useState(true);

  const handleLogoClick = useCallback(() => {
    navigate(homePath.replace(ROUTE_ORG_ID_TEMPLATE, orgId?.toString() ?? ''));
  }, [homePath, navigate, orgId]);

  const handleHamburgerClick = useCallback(() => {
    setIsShowingNavOnMobile((prev) => !prev);
  }, [setIsShowingNavOnMobile]);

  /**
   * Tracks the actual viewport so the nav/hamburger visibility is driven by
   * JS state instead of solely by the CSS breakpoint override in
   * OpAppNav.scss/OpAppHeader.scss. Without this, closing the nav on mobile
   * and then widening the window can leave isShowingNavOnMobile stuck at
   * false with no way to reopen the nav.
   */
  const [isTabletOrWider, setIsTabletOrWider] = useState(
    () => globalThis.matchMedia(TABLET_NAV_MEDIA_QUERY).matches,
  );

  useEffect(() => {
    const mediaQuery = globalThis.matchMedia(TABLET_NAV_MEDIA_QUERY);
    const handleChange = (event: MediaQueryList | MediaQueryListEvent) => {
      setIsTabletOrWider(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const isShowingNav = isTabletOrWider || isShowingNavOnMobile;

  return (
    <OpLayout>
      <OpAppBanners banners={banners} />
      <OpLayout className="op-app-layout">
        <OpAppNav
          items={items}
          title={title}
          onLogoClick={handleLogoClick}
          setIsShowingNavOnMobile={setIsShowingNavOnMobile}
          hidden={!isShowingNav}
          footer={footer}
        />
        <OpLayout>
          <OpAppHeader
            showHamburger={!isShowingNav}
            onHamburgerClick={handleHamburgerClick}
            homePath={homePath}
            orgSwitcherProps={orgSwitcherProps}
          />
          <OpContent className="op-app-layout__content">
            <div className="op-app-layout__route">
              <Suspense fallback={<OpAppSuspenseLoader />}>
                <Outlet /> {/* Render content from child routes */}
              </Suspense>
            </div>
          </OpContent>
        </OpLayout>
      </OpLayout>
    </OpLayout>
  );
};
